<template>
  <div class="modern-form-container">
    <!-- Main Content Card -->
    <div class="form-card">
      <div class="card-header">
        <div class="header-icon">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
            />
            <polyline points="14,2 14,8 20,8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10,9 9,9 8,9" />
          </svg>
        </div>
        <h2 class="form-title">
          {{ t('admin-ui.vat_form_vat_number') }}
        </h2>
        <p class="form-subtitle">
          {{
            t('admin-ui.vat_form_select_country_in_which_your_company_is_based')
          }}
        </p>
      </div>

      <div class="card-body">
        <hit-form
          name="CheckVatForm"
          :value="objectFormData"
          :validations="validations"
          :force-cancel-button-hidden="true"
          :force-save-button-hidden="true"
        >
          <template #default="{formData, validationState}">
            <div class="form-grid">
              <!-- Country Selection -->
              <div class="input-group full-width">
                <hit-select
                  name="country"
                  :value="formData.country"
                  :label-fields="getDesignationForCurrentLocale"
                  :options="countries"
                  :clearable="false"
                  :label="t('admin-ui.country')"
                  :validation-state="validationState.country"
                  :filterable="true"
                  @change="onChangeCountry"
                />
              </div>

              <!-- VAT Number -->
              <div class="input-group full-width">
                <hit-input
                  class="vat-number-input"
                  name="vatNumber"
                  :value="formData.vatNumber"
                  :validation-state="validationState.vatNumber"
                  :label="t('admin-ui.vat_number')"
                  @input="onChangeVatNumber"
                />

                <div v-if="selectedCountry" class="vat-info">
                  <div class="info-item">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M9,12l2,2 4,-4" />
                    </svg>
                    <span
                      >{{ selectedCountry.vat_length }}
                      {{
                        t('admin-ui.vat_form_required_vat_number_length')
                      }}</span
                    >
                  </div>

                  <!-- Error message for symbols -->
                  <div v-if="vatErrorMessage" class="info-item error">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                    <span>{{ vatErrorMessage }}</span>
                  </div>

                  <div
                    v-if="objectFormData.vatNumber"
                    class="info-item"
                    :class="{
                      valid:
                        objectFormData.vatNumber.length ===
                        selectedCountry.vat_length,
                      invalid:
                        objectFormData.vatNumber.length !==
                        selectedCountry.vat_length,
                    }"
                  >
                    <svg
                      v-if="
                        objectFormData.vatNumber.length ===
                        selectedCountry.vat_length
                      "
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <polyline points="20,6 9,17 4,12" />
                    </svg>
                    <svg
                      v-else
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                    <span
                      >{{ objectFormData.vatNumber.length }}/{{
                        selectedCountry.vat_length
                      }}
                      {{ t('admin-ui.vat_form_vat_number_length') }}</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </template>
        </hit-form>

        <!-- Help Section -->
        <div class="help-section">
          <div class="help-header">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M9,9a3,3,0,1,1,6,0c0,2-3,3-3,3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <h3>{{ t('admin-ui.vat_form_country_not_listed') }}</h3>
          </div>
          <p>{{ t('admin-ui.vat_form_send_email_to_our_sales_department') }}</p>
          <a href="mailto:commercial@ficos.com" class="contact-link">
            {{ t('admin-ui.contact_us') }}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M7 17L17 7" />
              <path d="M7 7h10v10" />
            </svg>
          </a>
        </div>
      </div>
    </div>

    <!-- VAT Checking Dialog - Modern Loading Overlay -->
    <div v-if="isCheckingVatApi" class="vat-checking-overlay">
      <div class="vat-checking-modal">
        <div class="modal-content">
          <!-- Loading Spinner -->
          <div class="loading-spinner">
            <div class="spinner-ring">
              <div />
              <div />
              <div />
              <div />
            </div>
          </div>

          <!-- Header -->
          <div class="modal-header">
            <h3 class="modal-title">
              {{ t('admin-ui.stepper_checking_vat') }}
            </h3>
            <p class="modal-subtitle">
              {{ t('admin-ui.your_vat_number_is_checked') }}
            </p>
          </div>

          <!-- Security message -->
          <div class="security-notice">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
            <span>{{ t('admin-ui.vat-verification-security') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Banner -->
    <div v-if="showBanner" class="banner-container">
      <hit-banner :show-animation="showAnimationBanner" @close="closeBanner()">
        <template #dynamic-content>
          <h6>{{ textBanner }}</h6>
        </template>
      </hit-banner>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, PropType} from 'vue';
import {useI18n} from 'vue-i18n';
import {
  HitButton,
  HitDialog,
  HitForm,
  HitInput,
  HitPanel,
} from '@hit/components/src';
import HitSelect from '@hit/components/src/components/form/input/HitSelect.vue';
import {maxLength, minLength, required} from '@vuelidate/validators';
import TopBarNavigation from '../TopBarNavigation.vue';
import {MyUserCompany} from '../../objects/UserCompany';
import axios from 'axios';
import {Base64} from 'js-base64';
import HitBanner from '@hit/components/src/components/banner/HitBanner.vue';

type Country = {
  id: string;
  vat_length: number;
  designation?: string;
  designation_fr_be?: string;
  designation_nl_be?: string;
  designation_de_be?: string;
  designation_en_gb?: string;
  [key: string]: unknown;
};

// Validation personnalisée pour empêcher les symboles
const noSymbols = (value: string | null | undefined) => {
  if (!value) return true; // Champ vide est OK (c'est 'required' qui gère ça)
  return /^[a-zA-Z0-9]*$/.test(value); // Seulement lettres et chiffres
};

export default defineComponent({
  name: 'CompanyCheckForm',
  components: {
    TopBarNavigation,
    HitButton,
    HitSelect,
    HitPanel,
    HitInput,
    HitForm,
    HitDialog,
    HitBanner,
  },
  props: {
    nbrOfRequestApiMade: {
      type: Number,
      default: 0,
    },
    errorApi: {
      type: Boolean,
      required: true,
      default: false,
    },
    messageApi: {
      type: String,
      required: true,
      default: 'Bad VAT number',
    },
    isCheckingVatApi: {
      type: Boolean,
      required: false,
    },
    myObject: {
      type: Object as PropType<MyUserCompany>,
      required: true,
    },
    triggerValidation: {
      type: Number,
      default: 0,
    },
    showBanner: {
      type: Boolean,
      default: false,
      required: true,
    },
    textBanner: {
      type: String,
      default: '',
      required: true,
    },
    showAnimationBanner: {
      type: Boolean,
      default: false,
      required: true,
    },
  },
  emits: ['nextClick', 'previousClick', 'validationResult', 'close'],
  setup() {
    const {t, locale} = useI18n();
    return {t, locale};
  },
  data() {
    return {
      countryCode: '' as string,
      hasCountryCodeError: false,
      errorCountryCode: '' as string,
      vatNumber: '' as string,
      hasVatNumberError: true,
      errorVatNumber: '' as string,
      contentCountry: 'Click here to select a country' as string,
      contentVAT: 'Please type your VAT number' as string,
      dropdownAlreadyChanged: false,
      minLength: 1 as number,
      maxLength: 12 as number,
      accessToNextPageForced: false,
      objectFormData: {
        country: null,
        vatNumber: null,
      } as {country: string | null; vatNumber: string | null},
      countries: [] as Country[],
    };
  },
  computed: {
    validations() {
      return {
        country: {required},
        vatNumber: {
          required,
          minlength: minLength(this.maxLength),
          maxLength: maxLength(this.maxLength),
          noSymbols: noSymbols,
        },
      };
    },
    getDesignationForCurrentLocale() {
      if (!this.locale) {
        console.error('Locale is not defined.');
        return null;
      }

      const currentLocale = String(this.locale);
      const formattedLocale = currentLocale.replace('-', '_').toLowerCase();
      return [`designation_${formattedLocale}`];
    },
    selectedCountry() {
      for (let country of this.countries) {
        if (country.id === this.objectFormData.country) {
          return country;
        }
      }
      return null;
    },
    allowNextClick() {
      return (
        this.selectedCountry !== null &&
        this.objectFormData.vatNumber !== null &&
        this.objectFormData.vatNumber.length === this.selectedCountry.vat_length
      );
    },
    vatErrorMessage() {
      if (!this.objectFormData.vatNumber) return null;

      // Vérifier les symboles
      if (
        this.objectFormData.vatNumber &&
        /[^a-zA-Z0-9]/.test(this.objectFormData.vatNumber)
      ) {
        return this.t('admin-ui.vat-number-no-symbols-allowed');
      }

      // Vérifier la longueur si on a un pays sélectionné
      if (
        this.selectedCountry &&
        this.objectFormData.vatNumber.length !== this.selectedCountry.vat_length
      ) {
        return this.t('admin-ui.vat-number-length-error');
      }

      return null;
    },
  },
  mounted() {
    this.getCountries();
    // Restore VAT number immediately
    this.objectFormData.vatNumber = (this.myObject.vatNumber ?? null) as
      | string
      | null;
    // Country will be restored in the watch function when countries are loaded
  },
  watch: {
    triggerValidation(newVal: number, oldVal: number) {
      if (newVal > oldVal) {
        this.validateAndContinue();
      }
    },
    // Watch for countries loading to restore country selection
    countries(newCountries: Country[]) {
      if (
        newCountries &&
        newCountries.length > 0 &&
        this.myObject.countryCode
      ) {
        // Restore country selection after countries are loaded
        this.objectFormData.country = this.getCountryIdFromCountryCode(
          this.myObject.countryCode,
        );
        if (this.objectFormData.country) {
          this.maxLength = this.get_vat_length(this.objectFormData.country);
        }
      }
    },
  },
  updated() {
    if (this.nbrOfRequestApiMade >= 3) {
      this.accessToNextPageForced = true;
    }
  },
  methods: {
    get_vat_length(countryId: string): number {
      const country = this.countries.find((c) => c.id === countryId);
      return country ? country.vat_length : this.maxLength;
    },
    generateAdminApiBase64Token(): string {
      const concatCredentials: string = `${process.env.VUE_APP_ADMIN_API_BASIC_AUTH_USERNAME}:${process.env.VUE_APP_ADMIN_API_BASIC_AUTH_PASSWORD}`;
      return Base64.encode(concatCredentials);
    },
    async getCountries() {
      const token = this.generateAdminApiBase64Token();

      const config = {
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Basic ${token}`,
        },
      };

      let route = 'country/list';
      let url = process.env.VUE_APP_ADMIN_API_BASE_URL + route;

      try {
        const response = await axios.get(url, config);
        this.countries = response.data || [];
      } catch (error) {
        console.error('Error loading countries:', error);
        this.countries = [];
      }
    },
    onChangeCountry(value: string) {
      this.objectFormData.country = value;
      this.countryCode = value;
      for (const country of this.countries) {
        if (value === country.id) {
          this.maxLength = country.vat_length;
        }
      }
      if (this.objectFormData.vatNumber === null) {
        if (this.objectFormData.country !== 'IN') {
          this.objectFormData.vatNumber = value.substring(0, 2).toUpperCase();
        }
      } else if (this.objectFormData.vatNumber.length <= 1) {
        if (this.objectFormData.country !== 'IN') {
          this.objectFormData.vatNumber = value.substring(0, 2).toUpperCase();
        }
      } else {
        // this.objectFormData.vatNumber =
        //     value.substring(0, 2).toUpperCase() +
        //     this.objectFormData.vatNumber.substring(2);
        this.hasVatNumberError =
          this.objectFormData.vatNumber!.length > this.maxLength;
      }
    },
    onChangeVatNumber(value: any) {
      this.objectFormData.vatNumber = value;
    },
    getCountryIdFromCountryCode(countryCode: string) {
      if (!countryCode || !this.countries || this.countries.length === 0) {
        return null;
      }

      for (let country of this.countries) {
        if (
          country.id &&
          country.id.substring(0, 2).toUpperCase() === countryCode.toUpperCase()
        ) {
          return country.id;
        }
      }
      return null;
    },
    validateAndContinue() {
      if (typeof this.objectFormData.vatNumber === 'string') {
        this.objectFormData.vatNumber = this.objectFormData.vatNumber
          .trim()
          .toUpperCase();
      }

      // Check required fields individually
      let missingFields = [];

      if (!this.objectFormData.country) {
        missingFields.push(this.t('admin-ui.country').toLowerCase());
      }
      if (
        !this.objectFormData.vatNumber ||
        (typeof this.objectFormData.vatNumber === 'string' &&
          this.objectFormData.vatNumber.trim() === '')
      ) {
        missingFields.push(this.t('admin-ui.vat_number').toLowerCase());
      }

      // If any required fields are missing
      if (missingFields.length > 0) {
        let message;
        if (missingFields.length === 1) {
          if (!this.objectFormData.country) {
            message = `${this.t('admin-ui.please-select-your')} ${
              missingFields[0]
            }.`;
          } else {
            message = `${this.t('admin-ui.please-enter-your')} ${
              missingFields[0]
            }.`;
          }
        } else {
          message = `${this.t('admin-ui.please-select-your')} ${this.t(
            'admin-ui.country',
          ).toLowerCase()} ${this.t('admin-ui.and')} ${this.t(
            'admin-ui.enter-your',
          )} ${this.t('admin-ui.vat_number').toLowerCase()}.`;
        }

        this.$emit('validationResult', {
          success: false,
          message: message,
        });
        return;
      }

      // Check for symbols in VAT number
      if (
        this.objectFormData.vatNumber &&
        /[^a-zA-Z0-9]/.test(this.objectFormData.vatNumber)
      ) {
        this.$emit('validationResult', {
          success: false,
          message: this.t('admin-ui.vat-number-no-symbols-allowed'),
        });
        return;
      }

      // Check VAT number length
      const selected = this.selectedCountry;
      if (selected && this.objectFormData.vatNumber) {
        if (this.objectFormData.vatNumber.length !== selected.vat_length) {
          const countryName =
            selected.designation_fr_be ||
            selected.designation_nl_be ||
            selected.designation_de_be ||
            selected.designation_en_gb ||
            selected.designation;

          this.$emit('validationResult', {
            success: false,
            message: this.t('admin-ui.vat-number-length-error-specific', {
              length: selected.vat_length,
              country: countryName,
            }),
          });
          return;
        }
      }

      // All validation passed, emit nextClick to trigger actual form submission
      this.$emit('nextClick', {
        countryCode: this.objectFormData.country!.substring(0, 2).toUpperCase(),
        vatNumber: this.objectFormData.vatNumber,
      });
      this.$emit('validationResult', {success: true});
    },
    closeBanner() {
      this.$emit('close');
    },
  },
});
</script>

<style scoped>
.modern-form-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 3rem 1rem 1rem 1rem;
  background: #f1f5f9;
  position: relative;
}

.form-card {
  background: #ffffff;
  border-radius: 1rem;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  padding: 0;
  max-width: 600px;
  width: 100%;
  overflow: hidden;
  position: relative;
  border: 1px solid #e5e7eb;
}

.card-header {
  background: #0d3749;
  color: white;
  padding: 1.5rem;
  text-align: center;
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  margin-bottom: 0.75rem;
}

.form-title {
  font-size: 1.375rem;
  font-weight: 600;
  margin: 0 0 0.375rem 0;
  line-height: 1.3;
}

.form-subtitle {
  font-size: 0.9rem;
  margin: 0;
  opacity: 0.9;
  font-weight: 400;
}

.card-body {
  padding: 1.5rem;
}

.form-grid {
  display: grid;
  gap: 1.25rem;
  grid-template-columns: 1fr;
}

.input-group {
  position: relative;
}

.input-group.full-width {
  grid-column: 1 / -1;
}

.vat-info {
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 0.375rem;
  border: 1px solid #e5e7eb;
  text-align: left;
}

.vat-info .info-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
}

.vat-info .info-item:not(:last-child) {
  margin-bottom: 0.5rem;
}

.vat-info .info-item.valid {
  color: #059669;
}

.vat-info .info-item.invalid {
  color: #dc2626;
}

.vat-info .info-item.error {
  color: #dc2626;
}

.vat-info .info-item svg {
  flex-shrink: 0;
}

.help-section {
  margin-top: 2rem;
  padding: 1.25rem;
  background: #f0f9ff;
  border-radius: 0.75rem;
  border: 1px solid #e0f2fe;
}

.help-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.help-header svg {
  color: #0ea5e9;
  flex-shrink: 0;
}

.help-header h3 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: #0c4a6e;
}

.help-section p {
  margin: 0 0 1rem 0;
  font-size: 0.875rem;
  color: #475569;
  line-height: 1.5;
}

.contact-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #0ea5e9;
  font-weight: 500;
  font-size: 0.875rem;
  text-decoration: none;
  transition: all 0.2s ease;
}

.contact-link:hover {
  color: #0284c7;
  text-decoration: underline;
}

.contact-link svg {
  flex-shrink: 0;
}

/* Responsive Design */
@media (min-width: 768px) {
  .modern-form-container {
    padding: 4rem 1rem 1rem 1rem;
  }

  .form-title {
    font-size: 1.5rem;
  }

  .form-subtitle {
    font-size: 1rem;
  }

  .card-header {
    padding: 1.75rem;
  }

  .card-body {
    padding: 1.75rem;
  }
}

@media (max-width: 768px) {
  .modern-form-container {
    min-height: auto;
    padding: 0.5rem 1rem 0.25rem 1rem;
    justify-content: center;
  }

  .form-card {
    max-width: 100%;
    border-radius: 0.75rem;
  }

  .card-header {
    padding: 1rem;
  }

  .card-body {
    padding: 1rem;
  }

  .form-title {
    font-size: 1.25rem;
  }

  .form-subtitle {
    font-size: 0.875rem;
  }

  .header-icon {
    width: 36px;
    height: 36px;
  }

  .form-grid {
    gap: 1rem;
  }

  .help-section {
    margin-top: 1.5rem;
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .modern-form-container {
    min-height: auto;
    padding: 0.25rem 0.75rem 0.125rem 0.75rem;
  }

  .form-card {
    border-radius: 0.5rem;
  }

  .card-header {
    padding: 1rem;
  }

  .card-body {
    padding: 1rem;
  }

  .form-title {
    font-size: 1.125rem;
  }

  .form-subtitle {
    font-size: 0.875rem;
  }

  .header-icon {
    width: 36px;
    height: 36px;
  }

  .vat-info {
    margin-top: 0.5rem;
    padding: 0.5rem;
  }

  .help-section {
    margin-top: 1rem;
    padding: 0.875rem;
  }
}

/* VAT Checking Modal Styles */
.vat-checking-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease-out;
}

.vat-checking-modal {
  background: rgba(255, 255, 255, 0.98);
  border-radius: 1.5rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  max-width: 480px;
  width: 90%;
  margin: 1rem;
  overflow: hidden;
  animation: slideInUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.modal-content {
  padding: 2.5rem 2rem;
  text-align: center;
  background: linear-gradient(
    135deg,
    rgba(248, 250, 252, 0.6) 0%,
    rgba(255, 255, 255, 0.8) 100%
  );
  border-radius: 1.4rem;
}

.loading-spinner {
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;
}

.spinner-ring {
  display: inline-block;
  position: relative;
  width: 64px;
  height: 64px;
}

.spinner-ring div {
  box-sizing: border-box;
  display: block;
  position: absolute;
  width: 51px;
  height: 51px;
  margin: 6px;
  border: 6px solid rgba(13, 55, 73, 0.8);
  border-radius: 50%;
  animation: spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  border-color: rgba(13, 55, 73, 0.8) transparent transparent transparent;
}

.spinner-ring div:nth-child(1) {
  animation-delay: -0.45s;
}

.spinner-ring div:nth-child(2) {
  animation-delay: -0.3s;
}

.spinner-ring div:nth-child(3) {
  animation-delay: -0.15s;
}

.modal-header {
  margin-bottom: 2rem;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0d3749;
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
}

.modal-subtitle {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
  font-weight: 400;
}

.security-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  background: rgba(16, 185, 129, 0.1);
  border-radius: 0.75rem;
  border: 1px solid rgba(16, 185, 129, 0.2);
  font-size: 0.875rem;
  color: #059669;
  font-weight: 500;
}

.security-notice svg {
  flex-shrink: 0;
}

.vat-number-input :deep(input) {
  text-transform: uppercase;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideInUp {
  0% {
    opacity: 0.3;
    transform: translateY(40px) scale(0.95);
  }
  60% {
    opacity: 0.9;
    transform: translateY(-8px) scale(1.01);
  }
  80% {
    transform: translateY(3px) scale(0.995);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Mobile responsive for modal */
@media (max-width: 480px) {
  .vat-checking-modal {
    width: 95%;
    border-radius: 1rem;
  }

  .modal-content {
    padding: 1.5rem 1.25rem;
  }

  .modal-title {
    font-size: 1.25rem;
  }

  .modal-subtitle {
    font-size: 0.875rem;
  }

  .loading-spinner {
    margin-bottom: 1rem;
  }

  .spinner-ring {
    width: 48px;
    height: 48px;
  }

  .spinner-ring div {
    width: 38px;
    height: 38px;
    margin: 5px;
    border-width: 4px;
  }

  .security-notice {
    padding: 0.625rem;
    font-size: 0.8rem;
  }
}

.banner-container {
  margin-top: 1rem;
  max-width: 600px;
  width: 100%;
}
</style>
